/**
 * Per-tenant branding, baked at build time (Vite env → Dockerfile.bff
 * build-args). When a tenant logo is configured the shell shows it INSTEAD of
 * the Vera mark, and an optional tenant primary color re-anchors the whole
 * "tinta" scale (MUI palette + CSS vars); absent → Vera-only chrome and the
 * institutional ink blue (demo/local and untenanted builds look as before).
 */

const env = import.meta.env;

/** Accessible name of the tenant (alt text for the logo). */
export const TENANT_NAME: string = env.VITE_TENANT_NAME ?? "";

/** SPA-served path of the tenant logo (e.g. /tenants/colbach.png), or "". */
export const TENANT_LOGO: string = env.VITE_TENANT_LOGO ?? "";

/** Tenant primary color (hex, e.g. #008876), or "" to keep Vera's tinta. */
export const TENANT_PRIMARY: string = /^#[0-9a-fA-F]{6}$/.test(env.VITE_TENANT_PRIMARY ?? "")
  ? (env.VITE_TENANT_PRIMARY as string)
  : "";

export const TENANT_BRANDED = Boolean(TENANT_LOGO);

/* ---- Brand scale (tinta-shaped ramp derived from TENANT_PRIMARY) -------- */

export type BrandScale = Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>;

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/** Mix `hex` toward `target` ([r,g,b]) by `pct` (0..1), returning hex. */
function mix(hex: string, target: [number, number, number], pct: number): string {
  const [r, g, b] = hexToRgb(hex);
  const ch = (c: number, t: number) => Math.round(c + (t - c) * pct);
  return `#${[ch(r, target[0]), ch(g, target[1]), ch(b, target[2])]
    .map((c) => c.toString(16).padStart(2, "0"))
    .join("")}`;
}

/** `rgba()` string from hex + alpha, for focus rings / hovers / selection. */
export function brandAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const WHITE: [number, number, number] = [255, 255, 255];
const BLACK: [number, number, number] = [0, 0, 0];

/**
 * Derive a 50–900 ramp from one anchor color. Institutional logo colors tend
 * to sit around the 500–600 lightness band, so the darker steps (600–900)
 * shade progressively past the anchor — keeping the 700 slot (the primary
 * anchor, per tinta) dark enough for AA contrast with white text.
 */
export function buildBrandScale(anchor: string): BrandScale {
  return {
    50: mix(anchor, WHITE, 0.93),
    100: mix(anchor, WHITE, 0.84),
    200: mix(anchor, WHITE, 0.68),
    300: mix(anchor, WHITE, 0.47),
    400: mix(anchor, WHITE, 0.27),
    500: mix(anchor, WHITE, 0.12),
    600: mix(anchor, BLACK, 0.1),
    700: mix(anchor, BLACK, 0.22),
    800: mix(anchor, BLACK, 0.38),
    900: mix(anchor, BLACK, 0.55),
  };
}

/** The tenant scale, or null when no tenant primary is configured. */
export const tenantBrandScale: BrandScale | null = TENANT_PRIMARY
  ? buildBrandScale(TENANT_PRIMARY)
  : null;

/**
 * Apply tenant branding to the document: re-anchor the `--tinta-*` CSS vars
 * on :root so every `var(--brand*)` / token consumer follows the tenant
 * color, and retitle the tab. Call once before the first render; no-op on
 * unbranded builds.
 */
export function applyTenantBranding(): void {
  if (TENANT_BRANDED) {
    document.title = TENANT_NAME
      ? `${TENANT_NAME} · Consola de validación de documentos`
      : "Consola de validación de documentos";
  }
  if (!tenantBrandScale) return;
  const root = document.documentElement;
  for (const [step, value] of Object.entries(tenantBrandScale)) {
    root.style.setProperty(`--tinta-${step}`, value);
  }
  root.style.setProperty("--ring", brandAlpha(tenantBrandScale[500], 0.35));
}
